import { useState } from "react";

import type { SourceHealth, SourceKind } from "../contract/types";
import { formatIsoTime } from "./format";
import { usePersistentString } from "./persistentUi";
import { useLiveSources } from "./useLiveSources";

/**
 * État dégradé visible (F4.1, CDC §2.3). Quand une source cesse de publier, on
 * dit quelles lignes sont retombées à l'horaire théorique et depuis quand — ni
 * écran vide, ni temps réel qui ment. Alimenté par le sujet `sources` du flux.
 *
 * Informatif, pas permanent : réduit à une ligne par défaut, dépliable pour le
 * détail, masquable. Le masquage est retenu (localStorage) jusqu'au prochain
 * vidage du cache, mais ne vaut que pour l'ensemble dégradé courant — si une
 * nouvelle source tombe (signature différente), la bannière revient.
 */
export function DegradedBanner() {
  const { sources } = useLiveSources();

  const degraded = sources.filter((s) => s.status !== "ok" || s.fallback !== "none");
  const signature = degraded
    .map((s) => s.sourceId)
    .sort()
    .join("|");

  const [expanded, setExpanded] = useState(false);
  const [dismissedSignature, setDismissedSignature] = usePersistentString("sentinelle.degradedBanner.dismissed");

  if (degraded.length === 0) return null;
  // Le masquage ne tient que pour la signature masquée : une signature
  // différente (nouvelle source dégradée, ou une qui se rétablit) rouvre.
  if (dismissedSignature === signature) return null;

  const affectedLines = new Set(degraded.flatMap((s) => s.affectedLineIds));

  return (
    <div
      className="notification is-warning is-light"
      role="status"
      aria-live="polite"
      style={{
        margin: 0,
        borderRadius: 0,
        padding: "6px 16px",
        fontSize: "0.78rem",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <span className="ovl" style={{ color: "var(--sentinelle-warn)" }}>
          Sources dégradées
        </span>{" "}
        {degraded.length} source{degraded.length > 1 ? "s" : ""}
        {affectedLines.size > 0
          ? ` · ${affectedLines.size} ligne${affectedLines.size > 1 ? "s" : ""} à l'horaire théorique`
          : ""}
        {expanded ? (
          <ul style={{ marginTop: 4 }}>
            {degraded.map((source) => (
              <li key={source.sourceId}>{describeSource(source)}</li>
            ))}
          </ul>
        ) : null}
      </div>
      <button
        type="button"
        className="button is-small is-white"
        style={{ height: 30, padding: "0 7px", fontSize: "0.7rem", flex: "none" }}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? "Réduire" : "Détails"}
      </button>
      <button
        type="button"
        className="button is-small is-white"
        style={{ height: 30, padding: "0 7px", fontSize: "0.7rem", flex: "none" }}
        aria-label="Masquer la bannière d'état dégradé"
        title="Masquer — revient si une autre source se dégrade"
        onClick={() => setDismissedSignature(signature)}
      >
        ✕
      </button>
    </div>
  );
}

const KIND_LABEL: Record<SourceKind, string> = {
  "vehicle-positions": "positions véhicules",
  "trip-updates": "horaires temps réel",
  alerts: "alertes",
  "gtfs-static": "GTFS statique",
};

function describeSource(source: SourceHealth): string {
  const label = `${source.operatorId} · ${KIND_LABEL[source.kind]}`;
  const lines = source.affectedLineIds.map(shortLine).join(", ");
  const since = source.since ? ` depuis ${formatIsoTime(source.since)}` : "";
  const status =
    source.status === "empty"
      ? "ne publie plus aucune donnée"
      : source.status === "failing"
        ? "répond par intermittence"
        : source.status === "down"
          ? "est injoignable"
          : "signale un incident";
  const fallback =
    source.fallback === "scheduled"
      ? ` — ${lines ? `lignes ${lines}` : "lignes concernées"} affichées à l'horaire théorique`
      : "";
  return `${label} ${status}${since}${fallback}.`;
}

/** `TCAR:91` → `91`. */
function shortLine(id: string): string {
  const i = id.indexOf(":");
  return i >= 0 ? id.slice(i + 1) : id;
}
